import React from "react";
import { MapPin, Gauge, ArrowUpRight } from "lucide-react";
import { FeaturedVehicle } from "../data/featuredVehicles";
import { carouselSection } from "../content/landingContent";
import { useResourceImage } from "../lib/useResourceImage";
import { useVehicleAdUrl } from "../lib/useVehicleAdUrl";
import { useContentField } from "../lib/useContent";

interface VehicleCardProps {
  vehicle: FeaturedVehicle;
}

export const VehicleCard: React.FC<VehicleCardProps> = ({ vehicle }) => {
  const { src } = useResourceImage(`vehicle.${vehicle.id}`, vehicle.image);
  const adUrl = useVehicleAdUrl(vehicle.id, vehicle.url);
  const viewAd = useContentField("carouselSection.viewAd", carouselSection.viewAd);

  return (
    <div
      data-testid={`vehicle-card-${vehicle.id}`}
      className="group h-full rounded-2xl border border-neutral-200 overflow-hidden bg-white hover:shadow-[0_20px_45px_-20px_rgba(0,0,0,0.25)] transition-shadow duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={src}
          alt={vehicle.title}
          loading="lazy"
          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {vehicle.badge && (
          <span className="absolute top-3 left-3 rounded-full bg-motor-red px-3 py-1 text-[11px] font-display font-semibold uppercase tracking-wide text-white">
            {vehicle.badge}
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display font-semibold text-base text-motor-ink">{vehicle.title}</h3>
        <p className="mt-1 font-display font-bold text-lg text-motor-red">{vehicle.price}</p>
        <div className="mt-3 flex flex-col gap-1.5 text-xs text-neutral-500">
          <span className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> {vehicle.location}
          </span>
          {vehicle.mileage && (
            <span className="flex items-center gap-1.5">
              <Gauge className="h-3.5 w-3.5" /> {vehicle.mileage}
            </span>
          )}
        </div>
        <a
          href={adUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`vehicle-view-ad-${vehicle.id}`}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-display font-semibold text-motor-ink hover:text-motor-red transition-colors"
        >
          {viewAd}
          <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
};
